import React, { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, authHeaders, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Share2, Check, Store, MapPin, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";

export default function InviteSheet({ open, onOpenChange, business }) {
  const { client } = useAuth();
  const [sent, setSent] = useState(false);
  const [count, setCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailLookup, setEmailLookup] = useState(false);

  // Auto-lookup public business email when the sheet opens
  useEffect(() => {
    if (!open || !business?.place_id) return;
    let cancelled = false;
    setEmail("");
    setEmailLookup(true);
    api
      .get("/invites/find-email", {
        params: { place_id: business.place_id, website: business.website || "", name: business.name || "", address: business.address || "" },
        headers: getToken("client") ? authHeaders("client") : {},
      })
      .then((r) => {
        if (cancelled) return;
        const found = (r.data && r.data.email) || "";
        if (found) setEmail(found);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setEmailLookup(false); });
    return () => { cancelled = true; };
  }, [open, business?.place_id, business?.website]);

  if (!business) return null;

  const refLink = client?.public_code ? `https://kouponow.com/?ref=${client.public_code}` : "https://kouponow.com";
  const shareText = `Ciao ${business.name}! 👋 Mi piacerebbe trovare le tue offerte su KoupoNow, l'app dei coupon dei negozi vicino a me. Ti unisci? 👉 ${refLink}`;

  const logInvite = async (channel) => {
    setLoading(true);
    try {
      const r = await api.post("/invites", {
        place_id: business.place_id, name: business.name, address: business.address || "",
        phone: business.phone || "", website: business.website || "", channel,
      }, { headers: getToken("client") ? authHeaders("client") : {} });
      setCount(r.data.count);
      setSent(true);
    } catch (_) { toast.error("Errore, riprova"); }
    finally { setLoading(false); }
  };

  const shareNative = async () => {
    await logInvite("share");
    if (navigator.share) {
      try { await navigator.share({ title: "KoupoNow", text: shareText, url: refLink }); } catch (_) {}
    } else {
      try { await navigator.clipboard.writeText(shareText); toast.success("Messaggio copiato"); }
      catch (_) { toast.success("Invito registrato"); }
    }
  };

  const sendEmail = async () => {
    const clean = email.trim().toLowerCase();
    if (!clean || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(clean)) { toast.error("Inserisci un'email valida"); return; }
    setEmailLoading(true);
    try {
      const r = await api.post("/invites/email", {
        place_id: business.place_id, name: business.name,
        to_email: clean, address: business.address || "", website: business.website || "",
      }, { headers: getToken("client") ? authHeaders("client") : {} });
      setCount(r.data.count);
      setSent(true);
      toast.success("Email di invito inviata!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito, riprova");
    } finally { setEmailLoading(false); }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setSent(false); setCount(null); setEmail(""); } }}>
      <SheetContent side="bottom" className="rounded-t-3xl max-w-3xl mx-auto max-h-[90vh] overflow-y-auto" data-testid="invite-sheet">
        <SheetHeader className="text-left"><SheetTitle className="text-xl font-extrabold">Invita su KoupoNow</SheetTitle></SheetHeader>
        <div className="py-4 space-y-4">
          <div className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50">
            <div className="h-12 w-12 rounded-xl bg-white flex items-center justify-center shrink-0"><Store className="h-6 w-6 text-[#E63946]" /></div>
            <div className="min-w-0">
              <p className="font-bold text-slate-800 truncate">{business.name}</p>
              {business.address && <p className="text-xs text-slate-400 truncate flex items-center gap-1"><MapPin className="h-3 w-3" /> {business.address}</p>}
            </div>
          </div>

          {sent ? (
            <div className="text-center py-2">
              <div className="h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-2"><Check className="h-7 w-7 text-emerald-500" /></div>
              <p className="font-bold text-slate-800">Grazie! Invito registrato</p>
              {count != null && <p className="text-slate-500 text-sm mt-1">{count} {count === 1 ? "persona ha" : "persone hanno"} chiesto questo negozio su KoupoNow.</p>}
            </div>
          ) : (
            <>
              <p className="text-slate-500 text-sm">Questo negozio non è ancora su KoupoNow. Il modo migliore per invitarlo è via <b>email</b>: più richieste arrivano, prima lo contattiamo!</p>

              {/* Email — primary channel */}
              <div className="space-y-2 rounded-2xl border-2 border-[#E63946]/15 bg-[#FFF1F2]/40 p-3">
                <p className="text-xs font-bold text-[#BE123C] uppercase tracking-wide flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5" /> Invita via email
                  {emailLookup && <span className="inline-flex items-center gap-1 text-[11px] text-slate-400 font-semibold normal-case tracking-normal"><Loader2 className="h-3 w-3 animate-spin" /> cerco online…</span>}
                </p>
                <div className="relative">
                  <Mail className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    type="email"
                    inputMode="email"
                    placeholder={emailLookup ? "Cerco l'email del negozio…" : "Email del negozio"}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 rounded-xl pl-9 bg-white"
                    data-testid="invite-email-input"
                  />
                </div>
                <Button onClick={sendEmail} disabled={emailLoading} data-testid="invite-email-send" className="w-full h-12 rounded-xl font-bold gap-2 bg-[#E63946] hover:bg-[#d32f3c]">
                  {emailLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Mail className="h-5 w-5" /> Invia invito via email</>}
                </Button>
                <p className="text-[11px] text-slate-400">Suggeriamo in automatico l'email di contatto che troviamo online. Puoi modificarla.</p>
              </div>

              {/* Secondary: share / interest */}
              <div className="grid grid-cols-1 gap-2 pt-1">
                <Button onClick={shareNative} disabled={loading} variant="outline" data-testid="invite-share" className="h-12 rounded-xl font-bold gap-2">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Share2 className="h-5 w-5" />} Condividi in altro modo
                </Button>
                <Button onClick={() => logInvite("interest")} disabled={loading} variant="ghost" data-testid="invite-interest" className="h-11 rounded-xl font-semibold text-slate-500">
                  Segnala solo il tuo interesse
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
